/**
 * Report Export Utilities
 * Helper functions for exporting reports to PDF and CSV
 */

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { IndustryProject } from '@/types/industry';

interface ProjectReportData {
  project: IndustryProject;
  budgetRevenue: number;
  actualRevenue: number;
  budgetCost: number;
  actualCost: number;
  actualProfit: number;
  actualMargin: number;
  clientName?: string;
}

/**
 * Export profitability report to CSV
 */
export function exportProfitabilityToCSV(
  data: ProjectReportData[],
  filename: string = 'relatorio-lucratividade.csv'
) {
  // CSV headers
  const headers = [
    'Projeto',
    'Cliente',
    'Data Início',
    'Status',
    'Receita Budget (R$)',
    'Receita Real (R$)',
    'Custo Budget (R$)',
    'Custo Real (R$)',
    'Lucro Real (R$)',
    'Margem Real (%)',
  ];

  // CSV rows
  const rows = data.map((item) => [
    item.project.name,
    item.clientName || '-',
    item.project.start_date ? format(new Date(item.project.start_date), 'dd/MM/yyyy') : '-',
    getStatusLabel(item.project.status),
    formatNumberCSV(item.budgetRevenue),
    formatNumberCSV(item.actualRevenue),
    formatNumberCSV(item.budgetCost),
    formatNumberCSV(item.actualCost),
    formatNumberCSV(item.actualProfit),
    formatNumberCSV(item.actualMargin),
  ]);

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  // Add BOM for Excel UTF-8 compatibility
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

  // Download
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export profitability report to PDF
 * Using browser print dialog for now (simpler, better compatibility)
 */
export function exportProfitabilityToPDF(
  data: ProjectReportData[],
  totals: {
    budgetRevenue: number;
    actualRevenue: number;
    budgetCost: number;
    actualCost: number;
    budgetProfit: number;
    actualProfit: number;
  }
) {
  const totalBudgetMargin = totals.budgetRevenue > 0 ? (totals.budgetProfit / totals.budgetRevenue) * 100 : 0;
  const totalActualMargin = totals.actualRevenue > 0 ? (totals.actualProfit / totals.actualRevenue) * 100 : 0;

  // Create printable HTML
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Por favor, permita pop-ups para exportar PDF');
    return;
  }

  const html = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Relatório de Lucratividade - ${format(new Date(), 'dd/MM/yyyy')}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, sans-serif;
      padding: 40px;
      font-size: 11pt;
    }
    h1 {
      font-size: 24pt;
      margin-bottom: 8px;
      color: #1a1a1a;
    }
    .subtitle {
      font-size: 12pt;
      color: #666;
      margin-bottom: 24px;
    }
    .summary {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-bottom: 32px;
      page-break-inside: avoid;
    }
    .summary-card {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 16px;
    }
    .summary-card h3 {
      font-size: 10pt;
      color: #666;
      margin-bottom: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .summary-card .row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
    }
    .summary-card .label {
      font-size: 10pt;
      color: #666;
    }
    .summary-card .value {
      font-size: 10pt;
      font-weight: 500;
    }
    .summary-card .value.large {
      font-size: 14pt;
      font-weight: 700;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 24px;
      page-break-inside: avoid;
    }
    th {
      background: #f9fafb;
      padding: 12px 8px;
      text-align: left;
      font-size: 9pt;
      font-weight: 600;
      border-bottom: 2px solid #e5e7eb;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    th.right, td.right { text-align: right; }
    td {
      padding: 10px 8px;
      border-bottom: 1px solid #f3f4f6;
      font-size: 10pt;
    }
    tr:hover { background: #f9fafb; }
    .project-name {
      font-weight: 500;
      color: #1a1a1a;
    }
    .project-date {
      font-size: 8pt;
      color: #9ca3af;
      margin-top: 2px;
    }
    .positive { color: #059669; font-weight: 500; }
    .negative { color: #dc2626; font-weight: 500; }
    .badge {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 8pt;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .badge.excellent { background: #dcfce7; color: #166534; }
    .badge.good { background: #e0e7ff; color: #3730a3; }
    .badge.warning { background: #fef3c7; color: #92400e; }
    .badge.poor { background: #fee2e2; color: #991b1b; }
    .footer {
      margin-top: 40px;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;
      font-size: 9pt;
      color: #9ca3af;
      text-align: center;
    }
    @media print {
      body { padding: 20px; }
      .summary { page-break-after: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
    }
  </style>
</head>
<body>
  <h1>Relatório de Lucratividade</h1>
  <div class="subtitle">Análise de Budget vs Realizado - Gerado em ${format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}</div>

  <div class="summary">
    <div class="summary-card">
      <h3>Receita Total</h3>
      <div class="row">
        <span class="label">Budget:</span>
        <span class="value">${formatCurrency(totals.budgetRevenue)}</span>
      </div>
      <div class="row">
        <span class="label">Realizado:</span>
        <span class="value large">${formatCurrency(totals.actualRevenue)}</span>
      </div>
    </div>

    <div class="summary-card">
      <h3>Lucro Total</h3>
      <div class="row">
        <span class="label">Budget:</span>
        <span class="value">${formatCurrency(totals.budgetProfit)}</span>
      </div>
      <div class="row">
        <span class="label">Realizado:</span>
        <span class="value large">${formatCurrency(totals.actualProfit)}</span>
      </div>
    </div>

    <div class="summary-card">
      <h3>Margem de Lucro</h3>
      <div class="row">
        <span class="label">Budget:</span>
        <span class="value">${totalBudgetMargin.toFixed(1)}%</span>
      </div>
      <div class="row">
        <span class="label">Realizado:</span>
        <span class="value large">${totalActualMargin.toFixed(1)}%</span>
      </div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Projeto</th>
        <th>Cliente</th>
        <th class="right">Receita Budget</th>
        <th class="right">Receita Real</th>
        <th class="right">Custo Real</th>
        <th class="right">Lucro Real</th>
        <th class="right">Margem %</th>
        <th>Performance</th>
      </tr>
    </thead>
    <tbody>
      ${data
        .map(
          (item) => `
        <tr>
          <td>
            <div class="project-name">${item.project.name}</div>
            ${item.project.start_date ? `<div class="project-date">${format(new Date(item.project.start_date), 'MMM yyyy', { locale: ptBR })}</div>` : ''}
          </td>
          <td>${item.clientName || '-'}</td>
          <td class="right">${formatCurrency(item.budgetRevenue)}</td>
          <td class="right">${formatCurrency(item.actualRevenue)}</td>
          <td class="right">${formatCurrency(item.actualCost)}</td>
          <td class="right ${item.actualProfit >= 0 ? 'positive' : 'negative'}">${formatCurrency(item.actualProfit)}</td>
          <td class="right">${item.actualMargin.toFixed(1)}%</td>
          <td><span class="badge ${getPerformanceClass(item.actualMargin)}">${getPerformanceLabel(item.actualMargin)}</span></td>
        </tr>
      `
        )
        .join('')}
    </tbody>
  </table>

  <div class="footer">
    Relatório gerado automaticamente pelo sistema monelab
  </div>

  <script>
    window.onload = function() {
      window.print();
      setTimeout(function() { window.close(); }, 100);
    };
  </script>
</body>
</html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

// Helper functions
function formatNumberCSV(num: number): string {
  return num.toFixed(2).replace('.', ',');
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    planning: 'Planejamento',
    active: 'Ativo',
    on_hold: 'Pausado',
    completed: 'Concluído',
    cancelled: 'Cancelado',
  };
  return labels[status] || status;
}

function getPerformanceClass(margin: number): string {
  if (margin >= 30) return 'excellent';
  if (margin >= 15) return 'good';
  if (margin >= 5) return 'warning';
  return 'poor';
}

function getPerformanceLabel(margin: number): string {
  if (margin >= 30) return 'Excelente';
  if (margin >= 15) return 'Boa';
  if (margin >= 5) return 'Atenção';
  return 'Crítica';
}
